import React, { useMemo, useCallback, useState } from "react";
import cn from "classnames";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import styles from "./style.module.scss";
import { LocalisedText } from "../../i18n";
import {
  PaymentMethodState,
  AddressFieldsState,
  isCurrency,
} from "../../types";
import {
  ReducedOrder,
  PaymentMethod,
  PaymentType,
  CustomerPaymentInitiateResponse,
  CustomerAddress,
} from "../../generated/graphql";
import {
  getTotalPriceFromOrderItems,
  getOrderCurrency,
} from "../../utils/price";
import BankTransferForm from "./BankTransferForm";
import CreditCardForm from "./CreditCardForm";
import DigitalWalletButton from "./DigitalWalletButton";

import { STRIPE_MINIMUM_AMOUNT_MAP } from "../../constants";

interface PaymentMethodFormProps {
  order: ReducedOrder;
  paymentMethodList: PaymentMethod[];
  paymentMethodState: PaymentMethodState;
  isDisabled: boolean;
  isWalletButtonDisabled: boolean;
  isLoading: boolean;
  addressList: CustomerAddress[];
  handleRemoveAddress: (a: CustomerAddress) => void;
  setIsLoading: (isLoading: boolean) => void;
  setPaymentMethodState: (p: PaymentMethodState) => void;
  uploadReceiptFile: (files: File[]) => void;
  handleRemoveAsset: (id: string) => void;
  initiatePayment: () => Promise<CustomerPaymentInitiateResponse | null>;
  paymentQueryPolling: (paymentId: string) => Promise<boolean>;
  onSuccess: () => void;
  onSubmit: () => Promise<void>;
}

const PaymentMethodForm: React.ForwardRefRenderFunction<
  HTMLDivElement,
  PaymentMethodFormProps
> = (props: PaymentMethodFormProps, ref: React.Ref<HTMLDivElement>) => {
  const {
    paymentMethodList,
    paymentMethodState,
    setPaymentMethodState,
    order,
    isDisabled,
    isWalletButtonDisabled,
    isLoading,
    addressList,
    setIsLoading,
    uploadReceiptFile,
    handleRemoveAsset,
    initiatePayment,
    paymentQueryPolling,
    onSuccess,
    onSubmit,
    handleRemoveAddress,
  } = props;
  const [supportDigitalWallet, setSupportDigitalWallet] = useState(false);

  const totalPrice = useMemo(() => getTotalPriceFromOrderItems(order.items), [
    order.items,
  ]);
  const currency = useMemo(() => getOrderCurrency(order), [order]);

  const stripeMinimumAmount = useMemo(() => {
    if (isCurrency(currency)) {
      return STRIPE_MINIMUM_AMOUNT_MAP[currency];
    }
    console.error(`Not supported currency ${currency}`);
    return 0;
  }, [currency]);

  const onSelectBankTranferClicked = useCallback(() => {
    const paymentMethod = paymentMethodList.find(
      p => p.type === PaymentType.Custom
    );
    if (!paymentMethod) return;
    setPaymentMethodState({
      type: "custom",
      id: paymentMethod.id,
      noteContent: paymentMethod.noteContent || "",
      receiptAttachments: [],
    });
  }, [paymentMethodList, setPaymentMethodState]);

  const onSelectCreditCardClicked = useCallback(() => {
    const paymentMethod = paymentMethodList.find(
      p => p.type === PaymentType.Stripe
    );
    if (!paymentMethod) return;
    const billingAddress: AddressFieldsState =
      addressList.length > 0
        ? {
            type: "reuse",
            id: addressList[0].id,
            country: addressList[0].country || "",
            address1: addressList[0].address1 || "",
            address2: addressList[0].address2 || "",
            saveForFutureUse: false,
          }
        : {
            type: "new",
            country: "HKG",
            address1: "",
            address2: "",
            saveForFutureUse: false,
          };
    setPaymentMethodState({
      type: "stripeCreditCard",
      id: paymentMethod.id,
      isCreditCardComplete: false,
      saveCreditCard: false,
      isUsingShipmentAddressForBilling: false,
      billingAddress,
    });
  }, [paymentMethodList, setPaymentMethodState, addressList]);

  const stripePromise = useMemo(() => {
    return loadStripe(
      paymentMethodList.find(p => p.type === PaymentType.Stripe)
        ?.publishableKey || "placeholder"
    );
  }, [paymentMethodList]);

  const stripePaymentId = useMemo(() => {
    return paymentMethodList.find(p => p.type === PaymentType.Stripe)?.id;
  }, [paymentMethodList]);

  const bankTransferPaymentId = useMemo(() => {
    return paymentMethodList.find(p => p.type === PaymentType.Custom)?.id;
  }, [paymentMethodList]);

  return (
    <Elements stripe={stripePromise}>
      <div ref={ref} className={styles.paymentMethod}>
        <div className={styles.title}>
          <LocalisedText id="view.paymentMethodForm.title" />
        </div>
        {stripePaymentId !== undefined && totalPrice >= stripeMinimumAmount && (
          <DigitalWalletButton
            order={order}
            totalPrice={totalPrice}
            stripePaymentId={stripePaymentId}
            setIsLoading={setIsLoading}
            initiatePayment={initiatePayment}
            paymentQueryPolling={paymentQueryPolling}
            setSupportDigitalWallet={setSupportDigitalWallet}
            isDisabled={isWalletButtonDisabled}
            isLoading={isLoading}
            onSuccess={onSuccess}
          />
        )}

        <div
          className={cn(styles.paymentOptions, {
            [styles.singlePaymentMethod]: paymentMethodList.length === 1,
          })}
        >
          {stripePaymentId !== undefined && totalPrice >= stripeMinimumAmount && (
            <div
              onClick={onSelectCreditCardClicked}
              className={cn(styles.paymentOption, {
                [styles.selected]:
                  paymentMethodState.type === "stripeCreditCard",
                [styles.smallSize]: supportDigitalWallet,
              })}
            >
              <div className={styles.creditCardImg} />
              <div className={styles.paytmentOptionText}>
                <LocalisedText id="paymentMethod.creditCard" />
              </div>
            </div>
          )}
          {bankTransferPaymentId !== undefined && (
            <div
              onClick={onSelectBankTranferClicked}
              className={cn(styles.paymentOption, {
                [styles.selected]: paymentMethodState.type === "custom",
                [styles.smallSize]: supportDigitalWallet,
              })}
            >
              <div className={styles.bankTransferImg} />
              <div className={styles.paytmentOptionText}>
                <LocalisedText id="paymentMethod.bankTransfer" />
              </div>
            </div>
          )}
        </div>
        {paymentMethodState.type === "custom" && (
          <BankTransferForm
            paymentMethodState={paymentMethodState}
            isLoading={isLoading}
            isDisabled={isDisabled}
            totalPrice={totalPrice}
            currency={currency}
            uploadReceiptFile={uploadReceiptFile}
            handleRemoveAsset={handleRemoveAsset}
            onSubmit={onSubmit}
          />
        )}
        {paymentMethodState.type === "stripeCreditCard" && (
          <CreditCardForm
            setIsLoading={setIsLoading}
            isDisabled={isDisabled}
            isLoading={isLoading}
            order={order}
            paymentMethodState={paymentMethodState}
            handleRemoveAddress={handleRemoveAddress}
            addressList={addressList}
            setPaymentMethodState={setPaymentMethodState}
            initiatePayment={initiatePayment}
            paymentQueryPolling={paymentQueryPolling}
            onSuccess={onSuccess}
          />
        )}
      </div>
    </Elements>
  );
};

export default React.forwardRef(PaymentMethodForm);
